import { useState, useEffect, useRef, useCallback } from 'react';

export interface TTSSettings {
  rate: number;      // 0.5 - 2
  pitch: number;     // 0 - 2
  voiceURI: string;  // 語音識別碼
}

const DEFAULT_SETTINGS: TTSSettings = {
  rate: 1,
  pitch: 1,
  voiceURI: '',
};

const STORAGE_KEY = 'reader-tts-settings';

export function useTTS(content: string) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [totalParagraphs, setTotalParagraphs] = useState(0);
  const [settings, setSettings] = useState<TTSSettings>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const paragraphsRef = useRef<string[]>([]);

  // 儲存設定
  const updateSettings = useCallback((partial: Partial<TTSSettings>) => {
    setSettings(prev => {
      const next = { ...prev, ...partial };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  // 播放指定段落
  const speakParagraphRef = useRef<(index: number) => void>();
  
  const speakParagraph = useCallback((index: number) => {
    const paragraphs = paragraphsRef.current;
    if (index >= paragraphs.length) {
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentIndex(0);
      return;
    }

    speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(paragraphs[index]);
    utterance.rate = settings.rate;
    utterance.pitch = settings.pitch;

    const voice = voices.find(v => v.voiceURI === settings.voiceURI);
    if (voice) utterance.voice = voice;

    utterance.onend = () => {
      speakParagraphRef.current?.(index + 1);
    };

    utterance.onerror = (e) => {
      if (e.error !== 'canceled') {
        console.error('TTS error:', e.error);
        setIsPlaying(false);
      }
    };

    utteranceRef.current = utterance;
    setCurrentIndex(index);
    speechSynthesis.speak(utterance);
  }, [settings, voices]);

  // Update ref when function changes
  useEffect(() => {
    speakParagraphRef.current = speakParagraph;
  }, [speakParagraph]);

  // 分段處理
  useEffect(() => {
    const paragraphs = content
      .split(/\n\n+/)
      .map(p => p.trim())
      .filter(p => p.length > 0);
    paragraphsRef.current = paragraphs;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTotalParagraphs(paragraphs.length);
  }, [content]);

  // 載入語音列表
  useEffect(() => {
    const loadVoices = () => {
      const available = speechSynthesis.getVoices();
      // 優先中文語音
      const zhVoices = available.filter(v => 
        v.lang.startsWith('zh') || v.lang.startsWith('cmn')
      );
      const otherVoices = available.filter(v => 
        !v.lang.startsWith('zh') && !v.lang.startsWith('cmn')
      );
      setVoices([...zhVoices, ...otherVoices]);

      // 自動選擇預設中文語音
      if (!settings.voiceURI && zhVoices.length > 0) {
        updateSettings({ voiceURI: zhVoices[0].voiceURI });
      }
    };

    loadVoices();
    speechSynthesis.addEventListener('voiceschanged', loadVoices);
    return () => speechSynthesis.removeEventListener('voiceschanged', loadVoices);
  }, [settings.voiceURI, updateSettings]);

  // 播放控制
  const play = useCallback(() => {
    if (isPaused) {
      speechSynthesis.resume();
      setIsPaused(false);
    } else {
      setIsPlaying(true);
      setIsPaused(false);
      speakParagraph(currentIndex);
    }
  }, [isPaused, currentIndex, speakParagraph]);

  const pause = useCallback(() => {
    speechSynthesis.pause();
    setIsPaused(true);
  }, []);

  const stop = useCallback(() => {
    speechSynthesis.cancel();
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentIndex(0);
  }, []);

  const skipTo = useCallback((index: number) => {
    setCurrentIndex(index);
    if (isPlaying) {
      speakParagraph(index);
    }
  }, [isPlaying, speakParagraph]);

  // 清理
  useEffect(() => {
    return () => {
      speechSynthesis.cancel();
    };
  }, []);

  return {
    isPlaying,
    isPaused,
    currentIndex,
    totalParagraphs,
    voices,
    settings,
    updateSettings,
    play,
    pause,
    stop,
    skipTo,
  };
}
